// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { billingAddressStyles } from "@bg/Overrides/Widgets/CheckoutShipping/BillingAddress";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    billTo: BillToModel;
    extendedStyles?: BillingAddressInfoDisplayStyles;
}

export interface BillingAddressInfoDisplayStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    addressGridItem?: GridItemProps;
    address?: AddressInfoDisplayStyles;
    headingText?: TypographyPresentationProps;
    addressDisplayAndFormGridItem?: GridItemProps;
}

export const billingAddressInfoDisplayStyles: BillingAddressInfoDisplayStyles = {
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
            display: block;
        `,
    },
    headingText: {
        variant: "h5",
    },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    container: {
        gap: 0,
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
};

const BillingAddressInfoDisplay = ({ billTo, extendedStyles }: OwnProps) => {
    const styles = mergeToNew(billingAddressInfoDisplayStyles, mergeToNew(billingAddressStyles, extendedStyles));
    return (
        <GridContainer {...styles.container} data-test-selector="billingAddressInfoDisplay_container">
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.headingText} as="h3">
                    {translate("Billing Address")}
                </Typography>
            </GridItem>
            <GridItem {...styles.addressDisplayAndFormGridItem}>
                <AddressInfoDisplay
                    {...billTo}
                    state={billTo.state ? billTo.state.abbreviation : undefined}
                    country={billTo.country ? billTo.country.abbreviation : undefined}
                    extendedStyles={styles.addressDisplay}
                />
            </GridItem>
        </GridContainer>
    );
};

export default BillingAddressInfoDisplay;
